import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { createProjectSchema } from '../schemas';
import { useCreateProject } from './useCreateProject';
import { useUpdateProject } from './useUpdateProject';
import type { Project } from '../types';
import type { CreateProjectInput } from '../schemas';

interface UseProjectFormControllerOptions {
  /** Present → edit mode, updates this project. Absent → create mode. */
  project?: Project;
  /** Resets the form to `project`'s values whenever this flips to `true`. Defaults to `true` for inline (always-mounted) usage. */
  open?: boolean;
  onSuccess?: () => void;
}

/**
 * Shared controller behind the project create/edit form — extracted so the
 * same fields can render both inside `ProjectFormDialog` (edit mode) and
 * inline inside `CreateProjectDialog`'s "blank project" tab (create mode),
 * without duplicating the create/update mutation wiring.
 */
export function useProjectFormController({
  project,
  open = true,
  onSuccess,
}: UseProjectFormControllerOptions) {
  const { t } = useTranslation('projects');
  const isEdit = !!project;

  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const isPending = createProject.isPending || updateProject.isPending;

  const schema = createProjectSchema(t);
  const { control, handleSubmit, reset, setValue, watch } = useForm<CreateProjectInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      color: undefined,
      description: '',
      startDate: undefined,
      endDate: undefined,
    },
  });

  const selectedColor = watch('color');

  useEffect(() => {
    if (open) {
      reset({
        name: project?.name ?? '',
        color: project?.color ?? undefined,
        description: project?.description ?? '',
        startDate: project?.startDate ?? undefined,
        endDate: project?.endDate ?? undefined,
      });
    }
  }, [open, project, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isEdit && project) {
        await updateProject.mutateAsync({ projectId: project.id, payload: data });
      } else {
        await createProject.mutateAsync(data);
      }
      onSuccess?.();
    } catch {
      // global handler shows error toast
    }
  });

  return { control, onSubmit, isPending, selectedColor, setValue, isEdit };
}
